import { useEffect, useMemo, useRef, useState } from 'react';
import { getAccessToken, authenticatedFetch as fetch } from '../lib/auth-context.js';
import { SearchableSelect } from './SearchableSelect.js';

export interface ClientSearchSelectOption {
  id: string;
  name: string;
  company: string | null;
  email: string | null;
  jurisdiction: string | null;
}

/**
 * Client picker backed by SERVER-side search (/v1/clients?search=). The old
 * pattern pre-fetched one page (limit ≤ the API's 100 cap) and filtered
 * client-side — every client past the first page was silently invisible.
 * Here the first page loads up front and typing re-queries the server
 * (debounced); the selected option is pinned from a cache so its label
 * survives page changes.
 */
export function ClientSearchSelect({ value, onChange, placeholder, className, wrapperClassName, required, disabled, all = true, status, initialOption }: {
  value: string;
  onChange: (value: string, client: ClientSearchSelectOption | null) => void;
  placeholder?: string;
  className?: string;
  /** Extra classes on the root div (e.g. width in a filter row). */
  wrapperClassName?: string;
  required?: boolean;
  disabled?: boolean;
  /** Include non-active clients (default true, matching the old pickers). */
  all?: boolean;
  /** Restrict to one lifecycle status (e.g. 'ACTIVE'); overrides `all`. */
  status?: string;
  /** Seed for edit forms: the already-linked client's label, shown even when
   *  it is not on the first fetched page. */
  initialOption?: ClientSearchSelectOption | null;
}) {
  const [page, setPage] = useState<ClientSearchSelectOption[]>([]);
  const [searching, setSearching] = useState(false);
  const [query, setQuery] = useState('');
  const cacheRef = useRef(new Map<string, ClientSearchSelectOption>(initialOption ? [[initialOption.id, initialOption]] : []));
  const seqRef = useRef(0);

  useEffect(() => {
    const seq = ++seqRef.current;
    const controller = new AbortController();
    const timer = setTimeout(() => {
      setSearching(true);
      const params = new URLSearchParams({ limit: '100', sortBy: 'name', sortOrder: 'asc' });
      if (status) params.set('status', status);
      else if (all) params.set('all', 'true');
      if (query.trim()) params.set('search', query.trim());
      fetch(`/v1/clients?${params.toString()}`, { signal: controller.signal, headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` } })
        .then(async (res) => {
          if (!res.ok) throw new Error(`Failed: ${res.statusText}`);
          const body = await res.json() as { ok: true; data: { items: ClientSearchSelectOption[] } };
          return body.data.items;
        })
        .then((items) => {
          if (seqRef.current !== seq) return; // a newer query is in flight
          for (const c of items) cacheRef.current.set(c.id, c);
          setPage(items);
        })
        .catch(() => { if (seqRef.current === seq) setPage([]); })
        .finally(() => { if (seqRef.current === seq) setSearching(false); });
    }, 250);
    return () => { clearTimeout(timer); controller.abort(); seqRef.current++; };
  }, [query, all, status]);

  const options = useMemo(() => {
    const opts = page.map((c) => ({ value: c.id, label: c.company ? `${c.company} — ${c.name}` : c.name }));
    if (value && !page.some((c) => c.id === value)) {
      const pinned = cacheRef.current.get(value);
      if (pinned) opts.unshift({ value: pinned.id, label: pinned.company ? `${pinned.company} — ${pinned.name}` : pinned.name });
    }
    return opts;
  }, [page, value]);

  return (
    <SearchableSelect
      value={value}
      onChange={(v) => onChange(v, cacheRef.current.get(v) ?? null)}
      placeholder={placeholder ?? 'Select client…'}
      options={options}
      onQueryChange={setQuery}
      emptyMessage={searching ? 'Searching…' : 'No matches.'}
      className={className}
      wrapperClassName={wrapperClassName}
      required={required}
      disabled={disabled}
    />
  );
}
